import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  BellRing,
  Check,
  CheckCircle2,
  ExternalLink,
  FileText,
  ImageIcon,
  Menu as MenuIcon,
  Newspaper,
  Share2,
  Sparkles,
  Wand2,
} from "lucide-react";
import { AccentButton, AuthScene, Button, Input, Loader, fireConfetti, type ConfettiVariant } from "@wa/ui";
import type { AuthLayoutSpec, FunnelEffectsSpec, CardBorderStyle } from "@wa/core/theme";
import { useI18n, useT } from "../i18n/I18nProvider";
import { getImportStatus, setImportNotify, type ImportPhase, type ImportState } from "../lib/import";
import { useWebPush } from "../lib/useWebPush";
import { ADMIN_URL, withLang } from "../lib/auth";
import { Logo } from "../components/Logo";
import { AppBackground } from "../components/AppBackground";
import { LanguageMenu } from "../components/LanguageMenu";
import { BuildingBackdrop } from "../components/BuildingBackdrop";

/**
 * ImportProgressPage - funnel path 3 (master plan §7.2 steps 4-5): the post-signup
 * "we are building your site" progress view plus its result. After the public
 * `/alusta` funnel has registered the WP import the user lands here; the page polls
 * the import state (`/account/sites/:id/import-status`), shows live deploy-log style
 * progress and finally the real "wow" stats (how many pages/images came over) plus a
 * before/after comparison and the CTAs ("Open the admin center" / "View the site").
 * This closes the open hole in E2 slice 1: the import no longer runs invisibly in
 * the background.
 */

const POLL_MS = 2000;
const MAX_FETCH_RETRIES = 4; // a network blip while polling must not kill the view at once

/** The steps are the backend's REAL import phases (`ImportState.phase`), in the
 *  backend's actual order: branding/logo -> pages (images come along inside the
 *  pages) -> posts -> menus -> finalizing. A step counts as "done" once the backend
 *  has moved PAST it, not by a cosmetic timer (the earlier UI ticked through a step
 *  every 1.8 s and the last step "took forever", because it measured the whole
 *  import). */
const PHASE_STEPS: ReadonlyArray<{ phase: ImportPhase; icon: typeof FileText; key: string }> = [
  { phase: "branding", icon: Sparkles, key: "import.stepLogo" },
  { phase: "pages", icon: FileText, key: "import.stepPages" },
  { phase: "posts", icon: Newspaper, key: "import.stepPosts" },
  { phase: "menus", icon: MenuIcon, key: "import.stepMenu" },
  { phase: "finalizing", icon: Wand2, key: "import.stepFinalize" },
];

/** Result card border style -> CSS class (see @wa/ui styles.css). */
const BORDER_CLASS: Record<CardBorderStyle, string> = {
  beam: "card-aurora-border-beam",
  animated: "card-aurora-border-animated",
  static: "card-aurora-border",
  none: "border border-border",
};

/**
 * At the completion beat (confetti) we take the racing border animation OFF the
 * card and put a calm aurora glow behind the whole card instead
 * (`.funnel-card-halo`). Animated borders (beam/animated) fall back to a static
 * edge here; "none" stays edgeless.
 */
const CELEBRATE_BORDER_CLASS: Record<CardBorderStyle, string> = {
  beam: "card-aurora-border",
  animated: "card-aurora-border",
  static: "card-aurora-border",
  none: "border border-border",
};

export function ImportProgressPage({
  siteId,
  onDone,
  authSpec,
  funnelSpec,
  defaultEmail,
}: {
  siteId: string;
  onDone: () => void;
  authSpec?: AuthLayoutSpec;
  /** Effects of the "alusta" funnel (confetti/glow/loading) - host `funnel` block. */
  funnelSpec?: FunnelEffectsSpec;
  /** Signed-in user's email - prefill for the "notify me when ready" opt-in. */
  defaultEmail?: string;
}) {
  const t = useT();
  const glow = authSpec?.glow ?? "aurora";
  // Funnel effects: sane defaults when the host has set nothing (confetti ON).
  const cardBorder: CardBorderStyle = funnelSpec?.cardBorder ?? "beam";
  const loadingEffect = funnelSpec?.loadingEffect ?? "orb";
  const confettiEnabled = funnelSpec?.confetti !== false;
  const confettiVariant = (funnelSpec?.confettiVariant ?? "realistic") as ConfettiVariant;
  const [state, setState] = useState<ImportState | null>(null);
  const [fatal, setFatal] = useState(false); // access error (e.g. 404) -> cannot poll
  const retriesRef = useRef(0);

  // ── Poll the import state until it is done or failed ────────────────────────
  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function poll() {
      try {
        const s = await getImportStatus(siteId);
        if (!alive) return;
        retriesRef.current = 0;
        setState(s);
        if (s.status === "queued" || s.status === "running") {
          timer = setTimeout(poll, POLL_MS);
        }
      } catch {
        if (!alive) return;
        // Transient network/server error: retry a few times before giving up.
        if (retriesRef.current < MAX_FETCH_RETRIES) {
          retriesRef.current += 1;
          timer = setTimeout(poll, POLL_MS);
        } else {
          setFatal(true);
        }
      }
    }

    void poll();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [siteId]);

  const status = state?.status;
  const building = status === "queued" || status === "running";
  // The completion view is the confetti beat: here we swap the racing border around
  // the card for a calm aurora glow behind it (see the BORDER_CLASS pick below).
  const celebrating = status === "done" || status === "none";

  // ── Confetti on completion (once) ───────────────────────────────────────────
  // Mirrors the aishop order-confirmation "wow" beat: once the import finishes
  // (done/none) and the host has allowed confetti, the chosen variant fires.
  // `fireConfetti` itself honours `prefers-reduced-motion` and loads the engine
  // lazily (SSR safe). The ref keeps polling re-renders from firing it again.
  // The "already fired" flag is set INSIDE the timeout, not next to it, so that a
  // StrictMode double-invoke can never eat the single shot: a flag set up front would
  // be true by the second setup while the first setup's cleanup had already cancelled
  // the only scheduled timeout. Today the mount pair bails out at the status guard
  // above (`state` is still null while the first poll is in flight, and StrictMode
  // doubles effects only on mount, not on the update that brings "done"), so this is
  // protection rather than a live fix - it keeps the shot safe if this view ever gets a
  // seeded or cached state that is already "done" on the first render. Same pattern as
  // web_admin Builder/steps/DoneStep.tsx, where `active` CAN be true at mount.
  const firedConfetti = useRef(false);
  useEffect(() => {
    if (firedConfetti.current) return;
    if (!confettiEnabled) return;
    if (status !== "done" && status !== "none") return;
    const id = setTimeout(() => {
      firedConfetti.current = true;
      void fireConfetti(confettiVariant);
    }, 300);
    return () => clearTimeout(id);
  }, [status, confettiEnabled, confettiVariant]);

  // ── Live deploy log: the steps follow the backend's REAL phase ───────────────
  // The poll brings `state.phase`; the active step is the running phase, earlier ones
  // are done. `queued` / a phase not reported yet -> the first step is active
  // (branding does come first). There is no timer: a step completes when the backend
  // moves past it. Counters (`state.progress`): pages/posts as `done/total`, images
  // only as a running count (nobody knows the total before the pages are parsed).
  const prog = state?.progress ?? null;
  const stepCounter = (phase: ImportPhase): string | null => {
    if (!prog) return null;
    switch (phase) {
      case "pages": {
        const pages = fmtCount(prog.pagesDone, prog.pagesTotal);
        const imgs = prog.imagesDone > 0 ? t("import.imagesCount", { n: prog.imagesDone }) : null;
        return [pages, imgs].filter(Boolean).join(" · ") || null;
      }
      case "posts":
        return fmtCount(prog.postsDone, prog.postsTotal);
      default:
        return null;
    }
  };
  const steps = PHASE_STEPS.map((s) => ({ icon: s.icon, label: t(s.key), counter: stepCounter(s.phase) }));
  const phaseIdx = PHASE_STEPS.findIndex((s) => s.phase === state?.phase);
  const stepIdx = Math.max(0, phaseIdx);
  // ETA - the backend's record-rate estimate; under a minute we round to words.
  const eta = prog?.etaSeconds ?? null;
  const etaText =
    eta == null ? null : eta >= 60 ? t("import.etaMinutes", { min: Math.max(1, Math.round(eta / 60)) }) : t("import.etaUnderMinute");
  // Whether the user saved the notify opt-in - then we show the "you may close this"
  // confirmation and must no longer show the contradicting "keep the window open" hint.
  const [notifyDone, setNotifyDone] = useState(false);

  const siteUrl = state?.host ? `https://${state.host}` : null;
  const editorUrl = withLang(`${ADMIN_URL}/sites/${encodeURIComponent(siteId)}`);

  let body: React.ReactNode;

  if (fatal) {
    body = (
      <Centered>
        <AlertTriangle className="size-8 text-destructive" />
        <h1 className="text-xl font-bold tracking-tight">{t("import.errorTitle")}</h1>
        <p className="text-sm text-muted-foreground">{t("import.errorBody")}</p>
        <AccentButton glow={glow} type="button" size="lg" className="w-full" onClick={onDone}>
          {t("import.toAccount")}
        </AccentButton>
      </Centered>
    );
  } else if (!state) {
    body = (
      <Centered>
        <Loader size="md" />
        <p className="text-sm text-muted-foreground">{t("import.connecting")}</p>
      </Centered>
    );
  } else if (state.status === "failed") {
    body = (
      <Centered>
        <AlertTriangle className="size-8 text-destructive" />
        <h1 className="text-xl font-bold tracking-tight">{t("import.failedTitle")}</h1>
        <p className="text-sm text-muted-foreground">{t("import.failedBody")}</p>
        <div className="flex w-full flex-col gap-2">
          <AccentButton glow={glow} type="button" size="lg" className="w-full" asChild>
            <a href={editorUrl}>{t("import.openEditor")}</a>
          </AccentButton>
          <Button type="button" variant="ghost" size="lg" className="w-full" onClick={onDone}>
            {t("import.toAccount")}
          </Button>
        </div>
      </Centered>
    );
  } else if (state.status === "done" || state.status === "none") {
    const stats = state.stats;
    body = (
      <div className="flex flex-col items-center gap-5 text-center">
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 18 }}
          className="inline-flex size-14 items-center justify-center rounded-2xl bg-primary/10 text-primary"
        >
          <CheckCircle2 className="size-8" />
        </motion.div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("import.doneTitle")}</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {state.status === "done" ? t("import.doneBody") : t("import.readyBody")}
          </p>
        </div>

        {stats && (stats.pages > 0 || stats.posts > 0 || stats.images > 0) && (
          <div className="grid w-full grid-cols-3 gap-2">
            <Stat icon={FileText} value={stats.pages} label={t("import.statPages")} />
            <Stat icon={Newspaper} value={stats.posts} label={t("import.statPosts")} />
            <Stat icon={ImageIcon} value={stats.images} label={t("import.statImages")} />
          </div>
        )}

        {/* Before/after slider (§7.2 step 5): a draggable screenshot comparison when
            Browser Rendering produced both images; otherwise falls back to URL cards. */}
        {state.shots?.before && state.shots?.after && (
          <BeforeAfterSlider
            before={state.shots.before}
            after={state.shots.after}
            beforeLabel={t("import.before")}
            afterLabel={t("import.after")}
            hint={t("import.compareHint")}
          />
        )}
        {(state.url || siteUrl) && (
          <div className="flex w-full items-stretch gap-2">
            {state.url && <BeforeAfter label={t("import.before")} host={hostOf(state.url)} href={state.url} muted />}
            {state.url && siteUrl && (
              <div className="flex items-center text-muted-foreground">
                <ArrowRight className="size-4" />
              </div>
            )}
            {siteUrl && <BeforeAfter label={t("import.after")} host={state.host!} href={siteUrl} />}
          </div>
        )}

        <div className="flex w-full flex-col gap-2 pt-1">
          <AccentButton glow={glow} type="button" size="lg" className="w-full" asChild>
            <a href={editorUrl}>
              {t("import.openEditor")}
              <ArrowRight className="ml-1.5 size-4" />
            </a>
          </AccentButton>
          {siteUrl && (
            <div className="flex w-full gap-2">
              <Button type="button" variant="outline" size="lg" className="flex-1" asChild>
                <a href={siteUrl} target="_blank" rel="noreferrer">
                  {t("import.viewSite")}
                  <ExternalLink className="ml-1.5 size-4" />
                </a>
              </Button>
              <ShareButton url={siteUrl} />
            </div>
          )}
          <Button type="button" variant="ghost" size="sm" className="w-full" onClick={onDone}>
            {t("import.toAccount")}
          </Button>
        </div>
      </div>
    );
  } else {
    // queued / running - live progress
    body = (
      <div className="flex flex-col items-center gap-4 text-center">
        {/* Heading plus body ON TOP of the "thinking" orb animation: the orb is the
            backdrop (absolute inset-0, centred), the text sits above it at z-10. The
            wrapper is absolute so we do not depend on overriding `.agrav-star`'s
            `position: relative` (cascade order plays no role). */}
        <div className="relative flex w-full flex-col items-center py-3">
          <span
            className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-90"
            aria-hidden="true"
          >
            <BuildingBackdrop effect={loadingEffect} />
          </span>
          <div className="relative z-10">
            <h1 className="text-2xl font-bold tracking-tight drop-shadow-[0_1px_10px_rgba(0,0,0,0.45)]">
              {t("import.buildingTitle")}
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">{t("import.buildingBody")}</p>
            {etaText && (
              <p className="mt-1 text-xs font-medium tabular-nums text-muted-foreground">{etaText}</p>
            )}
          </div>
        </div>

        <ul className="w-full space-y-1.5 text-left">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const active = i === stepIdx;
            const done = i < stepIdx;
            return (
              <li
                key={s.label}
                className={`flex items-center gap-2.5 rounded-lg border px-3 py-1.5 transition-all duration-300 ${
                  active
                    ? "border-primary/40 bg-primary/5"
                    : done
                      ? "border-border/50 bg-card/60"
                      : "border-border/40 bg-card/40 opacity-60"
                }`}
              >
                <span
                  className={`inline-flex size-6 shrink-0 items-center justify-center rounded-md ${
                    active || done ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                  }`}
                >
                  {done ? <CheckCircle2 className="size-3.5" /> : <Icon className="size-3.5" />}
                </span>
                <span className={`flex-1 text-[13px] ${active || done ? "font-medium" : "text-muted-foreground"}`}>
                  {s.label}
                </span>
                {/* Real counter (12/22 · 309 images) - running on the active step, final on a done one. */}
                {s.counter && (active || done) && (
                  <span className="text-[11px] tabular-nums text-muted-foreground">{s.counter}</span>
                )}
                {active && <Loader size="sm" />}
              </li>
            );
          })}
        </ul>

        {!notifyDone && <p className="text-xs text-muted-foreground">{t("import.buildingHint")}</p>}

        {/* "Notify me when ready" - the user can close the window and get an email or
            push notification once the import finishes. OFF by default (must be enabled
            by the user). Saving hides the "keep the window open" hint above (notifyDone). */}
        <NotifyOptIn siteId={siteId} defaultEmail={defaultEmail} onSaved={() => setNotifyDone(true)} />
      </div>
    );
  }

  return (
    <AuthScene
      layout={authSpec?.layout}
      background={<AppBackground spec={authSpec} />}
      logo={<Logo variant="full" className="h-11 w-auto text-foreground md:h-12" />}
      topRight={<LanguageMenu />}
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="relative">
          {/* Aurora glow behind the whole card - only at the completion beat (confetti).
              It renders BEHIND the card (the card is a later sibling with relative z-10),
              so only the aura spilling past the edges shows, not a wash over the card. */}
          {celebrating && <span className="funnel-card-halo" aria-hidden="true" />}
          <div
            className={`relative z-10 ${
              celebrating ? CELEBRATE_BORDER_CLASS[cardBorder] : BORDER_CLASS[cardBorder]
            } rounded-xl bg-card/95 p-6 shadow-soft`}
          >
            {body}
          </div>
        </div>
      </motion.div>
    </AuthScene>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col items-center gap-4 py-2 text-center">{children}</div>;
}

/**
 * The "notify me when ready" opt-in - email (prefilled, editable) plus optional
 * browser push. OFF by default: the user enters or confirms it themselves. Push needs
 * permission and a subscription (`useWebPush`); it is only offered when VAPID is
 * configured on the platform. Saving stores the preference (`setImportNotify`) and the
 * end of the background job sends the notice. That lets the user close the window while
 * the LIVE view on the page keeps working too.
 */
function NotifyOptIn({
  siteId,
  defaultEmail,
  onSaved,
}: {
  siteId: string;
  defaultEmail?: string;
  /** Called once the opt-in is saved - the parent hides the "keep the window open" hint. */
  onSaved?: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const push = useWebPush({ swPath: "/push-sw.js" });
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [wantPush, setWantPush] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);

  const pushOffered = push.supported && push.vapidAvailable;
  const canSubmit = email.trim().length > 0 || wantPush;

  const submit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(false);
    try {
      // Push selected -> ask permission and subscribe THIS device; returns whether it worked.
      let pushOk = false;
      if (wantPush && pushOffered) pushOk = await push.subscribe();
      const trimmed = email.trim();
      if (!trimmed && !pushOk) {
        // Neither channel took effect (e.g. push permission denied, email empty).
        setError(true);
        return;
      }
      await setImportNotify(siteId, { email: trimmed || undefined, push: pushOk, locale });
      setSaved(true);
      onSaved?.();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  if (saved) {
    return (
      <div className="flex w-full items-center gap-2.5 rounded-lg border border-primary/40 bg-primary/5 px-3.5 py-3 text-left text-sm">
        <BellRing className="size-4 shrink-0 text-primary" />
        <span className="font-medium">{t("import.notifySaved")}</span>
      </div>
    );
  }

  return (
    <div className="w-full rounded-lg border border-border/60 bg-card/60 p-3.5 text-left">
      <div className="mb-2.5 flex items-center gap-2">
        <Bell className="size-4 shrink-0 text-primary" />
        <span className="text-sm font-medium">{t("import.notifyPrompt")}</span>
      </div>
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("import.notifyEmailPlaceholder")}
        inputMode="email"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        maxLength={200}
      />
      {pushOffered && (
        <label className="mt-2.5 flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={wantPush}
            onChange={(e) => setWantPush(e.target.checked)}
            className="size-4 rounded border-border accent-primary"
          />
          {t("import.notifyPush")}
        </label>
      )}
      {error && <p className="mt-2 text-xs text-destructive">{t("import.notifyError")}</p>}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-3 w-full"
        onClick={() => void submit()}
        disabled={!canSubmit || busy}
      >
        {busy ? <Loader size="sm" /> : t("import.notifyCta")}
      </Button>
    </div>
  );
}

function Stat({ icon: Icon, value, label }: { icon: typeof FileText; value: number; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-lg border border-border/60 bg-card/70 px-2 py-3">
      <Icon className="size-4 text-primary" />
      <span className="text-lg font-bold tabular-nums">{value}</span>
      <span className="text-[11px] text-muted-foreground">{label}</span>
    </div>
  );
}

function BeforeAfter({ label, host, href, muted }: { label: string; host: string; href: string; muted?: boolean }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`flex min-w-0 flex-1 flex-col gap-0.5 rounded-lg border px-3 py-2 text-left transition-colors hover:bg-card ${
        muted ? "border-border/50 bg-card/50" : "border-primary/40 bg-primary/5"
      }`}
    >
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={`truncate text-sm font-semibold ${muted ? "text-muted-foreground" : "text-foreground"}`}>{host}</span>
    </a>
  );
}

/**
 * Before/after screenshot slider - the old WP site and the new Husly site stacked on
 * each other with a draggable divider (§7.2 step 5). Both images share an identical
 * aspect ratio (the backend shot them with the same viewport), so "after" is cut at the
 * divider with `clip-path`. Interaction: a transparent `range` input gives native
 * dragging, keyboard (arrow keys) and accessibility; the visible handle and line follow
 * its value.
 */
function BeforeAfterSlider({
  before,
  after,
  beforeLabel,
  afterLabel,
  hint,
}: {
  before: string;
  after: string;
  beforeLabel: string;
  afterLabel: string;
  hint: string;
}) {
  const [pct, setPct] = useState(50);
  return (
    <div className="w-full">
      <div className="relative w-full select-none overflow-hidden rounded-lg border border-border/60">
        <div className="relative aspect-[1280/900] w-full bg-muted">
          {/* BEFORE (full) */}
          <img
            src={before}
            alt={beforeLabel}
            draggable={false}
            className="absolute inset-0 size-full object-cover object-top"
          />
          {/* AFTER (clipped from the left up to the divider) */}
          <img
            src={after}
            alt={afterLabel}
            draggable={false}
            className="absolute inset-0 size-full object-cover object-top"
            style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}
          />
          {/* Labels in the corners */}
          <span className="absolute left-2 top-2 rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
            {beforeLabel}
          </span>
          <span className="absolute right-2 top-2 rounded bg-primary/85 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground">
            {afterLabel}
          </span>
          {/* Divider line plus handle */}
          <div className="pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white/90 shadow-[0_0_0_1px_rgba(0,0,0,0.15)]" style={{ left: `${pct}%` }}>
            <span className="absolute top-1/2 left-1/2 flex size-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-slate-700 shadow-md">
              <ArrowRight className="size-3 -mr-0.5 rotate-180" />
              <ArrowRight className="size-3 -ml-0.5" />
            </span>
          </div>
          {/* Transparent control - native dragging plus keyboard plus a11y */}
          <input
            type="range"
            min={0}
            max={100}
            value={pct}
            onChange={(e) => setPct(Number(e.target.value))}
            aria-label={hint}
            className="absolute inset-0 size-full cursor-ew-resize opacity-0"
          />
        </div>
      </div>
      <p className="mt-1.5 text-center text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

/**
 * Share button for the new site URL. On mobile the native share sheet
 * (`navigator.share`), elsewhere a clipboard copy (2 s confirmation). Neither technique
 * is mandatory, so missing support or a refusal just bails out quietly (no error shown).
 */
function ShareButton({ url }: { url: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const share = async () => {
    const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
    if (typeof nav.share === "function") {
      // Native share sheet; a user cancel (AbortError) simply bails out quietly.
      try {
        await nav.share({ url, title: t("import.shareTitle") });
      } catch {
        /* cancelled or failed - do not fall back to copying (avoids a double action) */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked - bail out quietly */
    }
  };
  return (
    <Button type="button" variant="outline" size="lg" onClick={() => void share()} aria-label={t("import.share")}>
      {copied ? <Check className="size-4 text-primary" /> : <Share2 className="size-4" />}
    </Button>
  );
}

/** Counter "12/22" when the total is known; otherwise just the running count (if >0).
 *  `done` is clamped into the total - the draft pass can slightly exceed the (published)
 *  total, and "23/22" would look broken. */
function fmtCount(done: number, total: number | null): string | null {
  if (total != null && total > 0) return `${Math.min(done, total)}/${total}`;
  return done > 0 ? String(done) : null;
}

/** URL -> host name (for display); on a malformed URL return the raw string. */
function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  }
}
